import { useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type { AppTypeDescriptor } from '@internal/plugin-waadoo-config-common';
import type { AppTypeDetail } from '../shared/appTypes';
import { compiledDescriptorOf } from '../shared/useAppTypeDescriptor';
import { loadAppType, useAppTypeDescriptors } from '../shared/templateRoles';
import { isSchemaObject } from '../waadooForm/schema';
import { creationSpecOf } from './creationModel';

/**
 * Which `AppType` a field of the generic creation form is for: the one the
 * template names (`ui:options.appType`), else the one the wizard resolved
 * (`formContext.appType`, `GenericProjectWizard`), else the one whose
 * `spec.templates.create` designates the wizard's template
 * (`formContext.templateRef`). Upstream's form passes neither: there, a
 * template names its type in `ui:options.appType`.
 */
export interface CreationAppTypeRef {
  appType?: string;
  templateRef?: string;
}

/** What a field reads of its `ui:options` and its `formContext`. */
export function creationAppTypeRef(
  uiOptions: { appType?: string } | undefined,
  formContext: unknown,
): CreationAppTypeRef {
  const context = isSchemaObject(formContext) ? formContext : {};
  const appType = uiOptions?.appType ?? context.appType;
  const templateRef = context.templateRef;
  return {
    ...(typeof appType === 'string' ? { appType } : {}),
    ...(typeof templateRef === 'string' ? { templateRef } : {}),
  };
}

export type CreationAppTypeState =
  | { status: 'loading' }
  /** No type named, none designating the template, or none of that name. */
  | { status: 'missing' }
  /** Unreadable, or a descriptor the engine refuses. */
  | { status: 'failed' }
  | {
      status: 'ready';
      detail: AppTypeDetail;
      descriptor: AppTypeDescriptor;
    };

/** `detail` compiled, as a state. */
function stateOf(
  detail: AppTypeDetail | undefined,
): Exclude<CreationAppTypeState, { status: 'loading' }> {
  if (!detail) return { status: 'missing' };
  const descriptor = compiledDescriptorOf(detail);
  return descriptor
    ? { status: 'ready', detail, descriptor }
    : { status: 'failed' };
}

/** The type of `templateRef` among `items` (`spec.templates.create`). */
export function creatorOf(
  items: readonly AppTypeDetail[],
  templateRef: string,
): AppTypeDetail | undefined {
  const wanted = templateRef.toLowerCase();
  return items.find(item => creationSpecOf(item.spec).create === wanted);
}

/**
 * The type of a creation form (`CreationAppTypeRef`), read through the
 * shared descriptors' cache (`loadAppType`); by template, every type is
 * read (`useAppTypeDescriptors`). Never the previous answer when the
 * reference changes.
 */
export function useCreationAppType(
  ref: CreationAppTypeRef,
): CreationAppTypeState {
  const { appType, templateRef } = ref;
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [named, setNamed] = useState<{
    name: string;
    state: Exclude<CreationAppTypeState, { status: 'loading' }>;
  }>();
  const byTemplate = useAppTypeDescriptors({
    enabled: appType === undefined && templateRef !== undefined,
  });

  useEffect(() => {
    if (appType === undefined) return undefined;
    let active = true;
    loadAppType({ fetchApi, discoveryApi }, appType).then(
      detail => {
        if (active) setNamed({ name: appType, state: stateOf(detail) });
      },
      () => {
        if (active) setNamed({ name: appType, state: { status: 'failed' } });
      },
    );
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, appType]);

  return useMemo((): CreationAppTypeState => {
    if (appType !== undefined) {
      return named?.name === appType ? named.state : { status: 'loading' };
    }
    if (templateRef === undefined) return { status: 'missing' };
    switch (byTemplate.status) {
      case 'ready':
        return stateOf(creatorOf(byTemplate.items, templateRef));
      case 'failed':
        return { status: 'failed' };
      default:
        return { status: 'loading' };
    }
  }, [appType, templateRef, named, byTemplate]);
}

/**
 * The compiled descriptor of `appType` for a validator, which has no hooks:
 * through the same cache. Undefined when it cannot be read or compiled.
 */
export async function loadCreationDescriptor(
  apis: { fetchApi: FetchApi; discoveryApi: DiscoveryApi },
  appType: string,
): Promise<AppTypeDescriptor | undefined> {
  try {
    const detail = await loadAppType(apis, appType);
    return detail ? compiledDescriptorOf(detail) : undefined;
  } catch {
    // Unreadable: the caller refuses (fail closed).
    return undefined;
  }
}
